import type {
  TaskCompleteApiData,
  TaskCompleteApiResponses,
  TaskCompleteCarTirePhotoDocumentationApiData,
  TaskCompleteCarTirePhotoDocumentationApiResponses,
  TaskCompleteCarTireSizeRegistrationApiData,
  TaskCompleteCarTireSizeRegistrationApiResponses,
  TaskCompleteCarTireTreadDepthMeasurementApiData,
  TaskCompleteCarTireTreadDepthMeasurementApiResponses,
  TaskCreateApiData,
  TaskCreateApiResponses,
  TaskDeleteApiData,
  TaskDeleteApiResponses,
  TaskEventListApiData,
  TaskEventListApiResponses,
  TaskGetApiData,
  TaskGetApiResponses,
  TaskGroupByBookingCustomerApiData,
  TaskGroupByBookingCustomerApiResponses,
  TaskGroupGetApiData,
  TaskGroupGetApiResponses,
  TaskGroupTasksReorderApiData,
  TaskGroupTasksReorderApiResponses,
  TaskListApiData,
  TaskListApiResponses,
  TaskPatchApiData,
  TaskPatchApiResponses,
  TaskReopenApiData,
  TaskReopenApiResponses,
  TaskStartApiData,
  TaskStartApiResponses,
  TaskTemplateCreateApiData,
  TaskTemplateCreateApiResponses,
  TaskTemplateDeleteApiData,
  TaskTemplateDeleteApiResponses,
  TaskTemplateEventListApiData,
  TaskTemplateEventListApiResponses,
  TaskTemplateGetApiData,
  TaskTemplateGetApiResponses,
  TaskTemplateGroupEventListApiData,
  TaskTemplateGroupEventListApiResponses,
  TaskTemplateGroupGetApiData,
  TaskTemplateGroupGetApiResponses,
  TaskTemplateGroupItemCreateApiData,
  TaskTemplateGroupItemCreateApiResponses,
  TaskTemplateGroupItemDeleteApiData,
  TaskTemplateGroupItemDeleteApiResponses,
  TaskTemplateGroupItemPatchApiData,
  TaskTemplateGroupItemPatchApiResponses,
  TaskTemplateGroupItemsReorderApiData,
  TaskTemplateGroupItemsReorderApiResponses,
  TaskTemplateListApiData,
  TaskTemplateListApiResponses,
  TaskTemplatePatchApiData,
  TaskTemplatePatchApiResponses,
  TaskUnableToCompleteApiData,
  TaskUnableToCompleteApiResponses,
  TaskUnableToCompleteReasonsApiData,
  TaskUnableToCompleteReasonsApiResponses
} from '@navio/nsdk';

import type { ApiInput } from '../../utils';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

// Tasks

export const getTaskUrls = {
  getPaginatedTasks: dataEndpoint<TaskListApiData, TaskListApiResponses>({
    getUrl: (queryParams) => apiUrl('tasks/', queryParams)
  }),
  getTask: dataEndpoint<TaskGetApiData, TaskGetApiResponses>({
    getUrl: ({ taskId }) => apiUrl(`tasks/${taskId}/`)
  }),
  postCreateTask: responseEndpoint<TaskCreateApiData, TaskCreateApiResponses>({
    getUrl: () => apiUrl('tasks/'),
    getBody: (data) => data
  }),
  patchTask: responseEndpoint<TaskPatchApiData, TaskPatchApiResponses>({
    getUrl: ({ taskId }) => apiUrl(`tasks/${taskId}/`),
    getBody: ({ taskId: _, ...body }) => body
  }),
  deleteTask: dataEndpoint<TaskDeleteApiData, TaskDeleteApiResponses>({
    getUrl: ({ taskId }) => apiUrl(`tasks/${taskId}/`)
  }),
  postTaskStart: responseEndpoint<TaskStartApiData, TaskStartApiResponses>({
    getUrl: ({ taskId }) => apiUrl(`tasks/${taskId}/start/`)
  }),
  postTaskComplete: responseEndpoint<TaskCompleteApiData, TaskCompleteApiResponses>({
    getUrl: ({ taskId }) => apiUrl(`tasks/${taskId}/complete/`),
    getBody: ({ completedAt }) => ({ completedAt })
  }),
  postTaskUnableToComplete: responseEndpoint<TaskUnableToCompleteApiData, TaskUnableToCompleteApiResponses>({
    getUrl: ({ taskId }) => apiUrl(`tasks/${taskId}/unable-to-complete/`),
    getBody: (data) => data
  }),
  postTaskReopen: responseEndpoint<TaskReopenApiData, TaskReopenApiResponses>({
    getUrl: ({ taskId }) => apiUrl(`tasks/${taskId}/reopen/`),
    getBody: (data) => data
  }),
  postTaskCompleteCarTireTreadDepthMeasurement: responseEndpoint<
    TaskCompleteCarTireTreadDepthMeasurementApiData,
    TaskCompleteCarTireTreadDepthMeasurementApiResponses
  >({
    getUrl: ({ taskId }) => apiUrl(`tasks/${taskId}/complete-car-tire-tread-depth-measurement/`),
    getBody: (data) => data
  }),
  postTaskCompleteCarTireSizeRegistration: responseEndpoint<
    TaskCompleteCarTireSizeRegistrationApiData,
    TaskCompleteCarTireSizeRegistrationApiResponses
  >({
    getUrl: ({ taskId }) => apiUrl(`tasks/${taskId}/complete-car-tire-size-registration/`),
    getBody: (data) => data
  }),
  postTaskCompleteCarTirePhotoDocumentation: responseEndpoint<
    TaskCompleteCarTirePhotoDocumentationApiData,
    TaskCompleteCarTirePhotoDocumentationApiResponses
  >({
    getUrl: ({ taskId }) => apiUrl(`tasks/${taskId}/complete-car-tire-photo-documentation/`),
    getHeaders: () => ({ 'Content-Type': 'multipart/form-data' }),
    getBody: ({ taskId: _taskId, ...body }) => body,
    skipBodyTransformation: true
  }),
  getPaginatedTaskTemplates: dataEndpoint<TaskTemplateListApiData, TaskTemplateListApiResponses>({
    getUrl: (queryParams) => apiUrl('task-templates/', queryParams)
  }),
  getTaskTemplate: dataEndpoint<TaskTemplateGetApiData, TaskTemplateGetApiResponses>({
    getUrl: ({ taskTemplateId }) => apiUrl(`task-templates/${taskTemplateId}/`)
  }),
  postCreateTaskTemplate: responseEndpoint<TaskTemplateCreateApiData, TaskTemplateCreateApiResponses>({
    getUrl: () => apiUrl('task-templates/'),
    getBody: (data) => data
  }),
  patchTaskTemplate: responseEndpoint<TaskTemplatePatchApiData, TaskTemplatePatchApiResponses>({
    getUrl: ({ taskTemplateId }) => apiUrl(`task-templates/${taskTemplateId}/`),
    getBody: ({ taskTemplateId: _, ...body }) => body
  }),
  deleteTaskTemplate: dataEndpoint<TaskTemplateDeleteApiData, TaskTemplateDeleteApiResponses>({
    getUrl: ({ taskTemplateId }) => apiUrl(`task-templates/${taskTemplateId}/`)
  }),
  getTaskTemplateGroup: dataEndpoint<TaskTemplateGroupGetApiData, TaskTemplateGroupGetApiResponses>({
    getUrl: ({ taskTemplateGroupId }) => apiUrl(`task-template-groups/${taskTemplateGroupId}/`)
  }),
  getTaskGroup: dataEndpoint<TaskGroupGetApiData, TaskGroupGetApiResponses>({
    getUrl: ({ taskGroupId }) => apiUrl(`task-groups/${taskGroupId}/`)
  }),
  getTaskGroupByBookingCustomer: dataEndpoint<
    TaskGroupByBookingCustomerApiData,
    TaskGroupByBookingCustomerApiResponses
  >({
    getUrl: ({ bookingId }) => apiUrl(`task-groups/by-booking/${bookingId}/`)
  }),
  postCreateTaskTemplateGroupItem: responseEndpoint<
    TaskTemplateGroupItemCreateApiData,
    TaskTemplateGroupItemCreateApiResponses
  >({
    getUrl: ({ taskTemplateGroupId }) => apiUrl(`task-template-groups/${taskTemplateGroupId}/items/`),
    getBody: (data) => data
  }),
  patchTaskTemplateGroupItem: responseEndpoint<
    TaskTemplateGroupItemPatchApiData,
    TaskTemplateGroupItemPatchApiResponses
  >({
    getUrl: ({ taskTemplateGroupItemId }) => apiUrl(`task-template-group-items/${taskTemplateGroupItemId}/`),
    getBody: ({ taskTemplateGroupItemId: _, ...body }) => body
  }),
  deleteTaskTemplateGroupItem: dataEndpoint<
    TaskTemplateGroupItemDeleteApiData,
    TaskTemplateGroupItemDeleteApiResponses
  >({
    getUrl: ({ taskTemplateGroupItemId }) => apiUrl(`task-template-group-items/${taskTemplateGroupItemId}/`)
  }),
  postReorderTaskTemplateGroupItems: responseEndpoint<
    TaskTemplateGroupItemsReorderApiData,
    TaskTemplateGroupItemsReorderApiResponses
  >({
    getUrl: ({ taskTemplateGroupId }) => apiUrl(`task-template-groups/${taskTemplateGroupId}/items/reorder/`),
    getBody: (data) => data
  }),
  postReorderTaskGroupTasks: responseEndpoint<TaskGroupTasksReorderApiData, TaskGroupTasksReorderApiResponses>({
    getUrl: ({ taskGroupId }) => apiUrl(`task-groups/${taskGroupId}/tasks/reorder/`),
    getBody: (data) => data
  }),
  getPaginatedTaskEvents: dataEndpoint<TaskEventListApiData, TaskEventListApiResponses>({
    getUrl: (queryParams) => apiUrl('task-events/', queryParams)
  }),
  getPaginatedTaskTemplateEvents: dataEndpoint<TaskTemplateEventListApiData, TaskTemplateEventListApiResponses>({
    getUrl: (queryParams) => apiUrl('task-template-events/', queryParams)
  }),
  getPaginatedTaskTemplateGroupEvents: dataEndpoint<
    TaskTemplateGroupEventListApiData,
    TaskTemplateGroupEventListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('task-template-group-events/', queryParams)
  }),
  getTasks: dataEndpoint<TaskListApiData, TaskListApiResponses>({
    getUrl: (queryParams) => apiUrl('tasks/', queryParams)
  }),
  postTaskCompleteCarTirePhotoDocumentationMobile: responseEndpoint<
    TaskCompleteCarTirePhotoDocumentationApiData,
    TaskCompleteCarTirePhotoDocumentationApiResponses,
    Omit<ApiInput<TaskCompleteCarTirePhotoDocumentationApiData>, 'image'> & {
      imageData: { uri: string; name: string; type: string };
    }
  >({
    getUrl: ({ taskId }) => apiUrl(`tasks/${taskId}/complete-car-tire-photo-documentation/`),
    getBody: ({ imageData, carId, bookingItemId, season, wheelPosition, inspectionContext }) => {
      const formData = new FormData();
      formData.append('car_id', String(carId));
      if (bookingItemId != null) {
        formData.append('booking_item_id', String(bookingItemId));
      }
      formData.append('season', String(season));
      formData.append('wheel_position', String(wheelPosition));
      formData.append('inspection_context', String(inspectionContext));
      //@ts-expect-error React Native image shape for multipart
      formData.append('image', imageData);
      return formData;
    },
    getHeaders: () => ({
      'Content-Type': 'multipart/form-data'
    }),
    skipBodyTransformation: true
  }),
  getTaskUnableToCompleteReasons: dataEndpoint<
    TaskUnableToCompleteReasonsApiData,
    TaskUnableToCompleteReasonsApiResponses
  >({
    getUrl: (queryParams) => apiUrl('tasks/unable-to-complete-reasons/', queryParams)
  })
};
